import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import axios from "axios";
import { buildApiUrl } from "../utils/api";

const ENQUIRY_STATUSES = [
    'New enquiry',
    'Contacted',
    'Qualifying',
    'Quote shared',
    'Negotiation',
    'Won',
    'Lost',
    'On hold'
];

const STATUS_COLORS = {
    'New enquiry': '#0d6efd',
    'Contacted': '#6f42c1',
    'Qualifying': '#fd7e14',
    'Quote shared': '#0dcaf0',
    'Negotiation': '#ffc107',
    'Won': '#198754',
    'Lost': '#dc3545',
    'On hold': '#6c757d'
};

function CommonEnquiries() {
    const [enquiries, setEnquiries] = useState([]);
    const [filtered, setFiltered] = useState([]);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [toast, setToast] = useState("");
    const [expandedRow, setExpandedRow] = useState(null);
    const itemsPerPage = 10;

    const showToast = (msg) => {
        setToast(msg);
        setTimeout(() => setToast(""), 3500);
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const res = await axios.get(buildApiUrl("/api/enquiries/getEnquiries"));
                const data = Array.isArray(res.data) ? res.data : [];
                setEnquiries(data);
                setFiltered(data);
            } catch (error) {
                console.error("Error fetching enquiries", error);
            }
        };
        fetchData();
    }, []);

    useEffect(() => {
        const q = search.toLowerCase().trim();
        let result = enquiries;

        if (q) {
            result = result.filter(e =>
                (e.name || "").toLowerCase().includes(q) ||
                (e.email || "").toLowerCase().includes(q) ||
                (e.firmName || "").toLowerCase().includes(q) ||
                (e.subject || "").toLowerCase().includes(q)
            );
        }

        if (statusFilter) {
            result = result.filter(e => e.status === statusFilter);
        }

        setFiltered(result);
        setCurrentPage(1);
    }, [search, statusFilter, enquiries]);

    const indexOfLast = currentPage * itemsPerPage;
    const indexOfFirst = indexOfLast - itemsPerPage;
    const currentItems = filtered.slice(indexOfFirst, indexOfLast);
    const totalPages = Math.ceil(filtered.length / itemsPerPage);

    const handleStatusChange = async (enquiryId, newStatus) => {
        try {
            await axios.put(buildApiUrl(`/api/enquiries/updateStatus/${enquiryId}`), { status: newStatus });
            setEnquiries(prev => prev.map(e => e._id === enquiryId ? { ...e, status: newStatus } : e));
            showToast(`Status updated to "${newStatus}"`);
        } catch (error) {
            console.error("Error updating enquiry status", error);
            showToast("Failed to update status. Please try again.");
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "-";
        return new Date(dateStr).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
    };

    return (
        <>
            <Helmet>
                <title>Contact Enquiries | CS Agent</title>
            </Helmet>

            {toast && <div className="admin-toast">{toast}</div>}

            <div className="listHead">
                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                    <h1 className="mb-0">
                        Contact Enquiries{" "}
                        <span className="badge bg-secondary fs-6">{filtered.length}</span>
                    </h1>
                    <div className="d-flex gap-2 flex-wrap">
                        <input
                            type="text"
                            className="form-control"
                            style={{ minWidth: "220px" }}
                            placeholder="Search name, email, firm..."
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                        />
                        <select
                            className="form-select"
                            style={{ minWidth: "160px" }}
                            value={statusFilter}
                            onChange={e => setStatusFilter(e.target.value)}
                        >
                            <option value="">All statuses</option>
                            {ENQUIRY_STATUSES.map(s => (
                                <option key={s} value={s}>{s}</option>
                            ))}
                        </select>
                    </div>
                </div>

                <div className="table-responsive">
                    <table className="table table-bordered table-hover">
                        <thead className="custom-thead">
                            <tr>
                                <th>#</th>
                                <th>Date</th>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Phone</th>
                                <th>Firm</th>
                                <th>Subject</th>
                                <th>Service</th>
                                <th>Contact Method</th>
                                <th>Status</th>
                                <th>Details</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentItems.length > 0 ? (
                                currentItems.map((req, index) => (
                                    <React.Fragment key={req._id}>
                                        <tr>
                                            <td>{indexOfFirst + index + 1}</td>
                                            <td style={{ whiteSpace: "nowrap" }}>{formatDate(req.createdAt)}</td>
                                            <td>{req.name}</td>
                                            <td>
                                                <a href={`mailto:${req.email}`}>{req.email}</a>
                                            </td>
                                            <td>{req.contactNumber}</td>
                                            <td>{req.firmName}</td>
                                            <td style={{ maxWidth: "180px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                {req.subject}
                                            </td>
                                            <td>{req.serviceNeed || "-"}</td>
                                            <td>{req.preferredContactMethod || "-"}</td>
                                            <td>
                                                <select
                                                    className="form-select form-select-sm"
                                                    style={{ color: STATUS_COLORS[req.status] || "#000", minWidth: "140px" }}
                                                    value={req.status || "New enquiry"}
                                                    onChange={e => handleStatusChange(req._id, e.target.value)}
                                                >
                                                    {ENQUIRY_STATUSES.map(s => (
                                                        <option key={s} value={s}>{s}</option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td>
                                                <button
                                                    className="btn btn-sm btn-outline-secondary"
                                                    onClick={() => setExpandedRow(expandedRow === req._id ? null : req._id)}
                                                >
                                                    {expandedRow === req._id ? "Hide" : "View"}
                                                </button>
                                            </td>
                                        </tr>
                                        {expandedRow === req._id && (
                                            <tr className="table-light">
                                                <td colSpan="11">
                                                    <div className="p-3">
                                                        <div className="row g-3">
                                                            <div className="col-md-4">
                                                                <strong>Company Role:</strong> {req.companyRole || "-"}<br />
                                                                <strong>Target Markets:</strong> {req.targetMarkets || "-"}<br />
                                                                <strong>Monthly Order Band:</strong> {req.monthlyOrderBand || "-"}<br />
                                                                <strong>Go-Live Timeline:</strong> {req.goLiveTimeline || "-"}
                                                            </div>
                                                            <div className="col-md-8">
                                                                <strong>Message:</strong>
                                                                <p className="mt-1 mb-0" style={{ whiteSpace: "pre-wrap" }}>{req.message}</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="11" className="text-center text-muted py-4">No enquiries found</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <nav className="d-flex justify-content-center">
                        <ul className="pagination">
                            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                                <button className="page-link" onClick={() => setCurrentPage(p => p - 1)}>&laquo;</button>
                            </li>
                            {Array.from({ length: totalPages }, (_, i) => (
                                <li className={`page-item ${currentPage === i + 1 ? "active" : ""}`} key={i}>
                                    <button className="page-link" onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
                                </li>
                            ))}
                            <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                                <button className="page-link" onClick={() => setCurrentPage(p => p + 1)}>&raquo;</button>
                            </li>
                        </ul>
                    </nav>
                )}
            </div>
        </>
    );
}

export default CommonEnquiries;
